import {
  DeleteOutlined,
  EditOutlined,
  FileDownloadOutlined,
  LinkOutlined,
  LockOpenOutlined,
  LockOutlined,
  MoreHoriz,
  PushPinOutlined,
  SnippetFolderOutlined,
} from '@mui/icons-material';
import { Chip, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { PayloadAction } from '@reduxjs/toolkit';
import * as filesaver from 'file-saver';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Conversation, DownloadConversationResponse } from 'Types/conversation';

import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import {
  deleteConversation,
  downloadFile,
  getConversations,
  getFolderAgnosticConversations,
  updateConversation,
} from '../../redux/actions/conversationActions';
import { getGlobalSearchResults } from '../../redux/actions/globalSearchActions';
import { updateRelation } from '../../redux/actions/userSpecificRelationsActions';
import { myFolder } from '../../redux/consts';
import { selectConversations } from '../../redux/reducers/conversationReducer';
import { setShowConversationsOnly } from '../../redux/reducers/globalSearchReducer';
import { AppDispatch } from '../../redux/store';
import { IconLabel } from '../../theme/CustomComponents';
import { AppEnum } from '../../Types/enums';
import { Folder } from '../../Types/folder';
import { getHost } from '../../util/getHost';
import ShareDialog from '../Card/ShareDialog';
import DeleteConfirmationDialog from '../DeleteConfirmationDialog';
import MoveToDialog from '../Folder/FolderOptionsMenu/MoveToDialog';
import NameDialog from '../NameDialog';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';
import { useAuth } from '../Providers/AuthProvider';

const ChatOptionsMenu = ({
  conversation,
  refetchConversations,
}: {
  conversation: Conversation;
  refetchConversations?: () => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useClassicNavigate();
  const analytics = useAnalytics();
  const { currentUser } = useAuth();
  const { currentConversation } = useSelector(selectConversations);
  const { copySuccess, copyToClipboard } = useCopyToClipboard();

  const [anchorContextMenu, setAnchorContextMenu] = useState<null | HTMLElement>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [localShared, setLocalShared] = useState(conversation.shared);

  const handleOpenCardContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorContextMenu(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorContextMenu(null);
  };

  const handleConversationRename = (newTitle: string) => {
    analytics.event(
      AnalyticsEvent.WM_NIGEL_CONVERSATION_RENAME_CONFIRM,
      {
        id: conversation.id,
        value: newTitle,
      },
      {
        conversationId: conversation.id,
        conversationOldName: conversation.title,
        conversationNewName: newTitle,
      }
    );
    dispatch(
      updateConversation({
        updatedFields: { title: newTitle },
        conversationId: conversation.id,
      })
    ).then(() => {
      dispatch(setShowConversationsOnly(true));
      dispatch(getFolderAgnosticConversations({ pageAction: 'reset' }));
      dispatch(getConversations({ pageAction: 'reset' }));
      refetchConversations?.();
      dispatch(getGlobalSearchResults({ pageAction: 'reset' }));
      handleCloseUserMenu();
    });
  };

  const handleConversationPinToggle = () => {
    analytics.event(
      AnalyticsEvent.WM_NIGEL_CONVERSATION_PINNED,
      {
        id: conversation.id,
        value: conversation.pinned.toString(),
      },
      {
        conversationId: conversation.id,
        conversationWasPinned: conversation.pinned,
        conversationNowPinned: !conversation.pinned,
      }
    );
    dispatch(
      updateRelation({
        conversationId: conversation.id,
        pinned: !conversation.pinned,
      })
    ).then(() => {
      dispatch(setShowConversationsOnly(true));
      dispatch(getFolderAgnosticConversations({ pageAction: 'reset' }));
      dispatch(getConversations({ pageAction: 'reset' }));
      refetchConversations?.();
      dispatch(getGlobalSearchResults({ pageAction: 'reset' }));

      const pinningMessage = !conversation.pinned ? 'pinned' : 'unpinned';
      enqueueSnackbar(`Conversation ${pinningMessage}`, { variant: 'success' });
      handleCloseUserMenu();
    });
  };

  const handleConversationPrivacyToggle = useCallback(() => {
    const newPrivacyState = !conversation.isPrivate;
    analytics.event(
      conversation.appName === AppEnum.DEAL_GPT
        ? AnalyticsEvent.WM_IA_CONVERSATION_PRIVACY_TOGGLE
        : AnalyticsEvent.WM_NIGEL_CONVERSATION_PRIVACY_TOGGLE,
      {
        id: conversation.id,
        value: newPrivacyState.toString(),
      },
      {
        conversationId: conversation.id,
        conversationWasPrivate: conversation.isPrivate,
        conversationNowPrivate: newPrivacyState,
      }
    );

    dispatch(
      updateConversation({
        updatedFields: { isPrivate: newPrivacyState },
        conversationId: conversation.id,
      })
    ).then(() => {
      dispatch(setShowConversationsOnly(true));
      dispatch(getFolderAgnosticConversations({ pageAction: 'reset' }));
      dispatch(getConversations({ pageAction: 'reset' }));
      refetchConversations?.();
      dispatch(getGlobalSearchResults({ pageAction: 'reset' }));

      const privatizingMessage = !conversation.isPrivate ? 'is now private' : 'is now public';
      enqueueSnackbar(`Conversation ${privatizingMessage}`, { variant: 'success' });
      handleCloseUserMenu();
    });
  }, [conversation, dispatch, analytics, refetchConversations]);

  const handleConversationDelete = () => {
    analytics.event(
      AnalyticsEvent.WM_NIGEL_CONVERSATION_DELETE_CONFIRM,
      {
        id: conversation.id,
      },
      { conversationId: conversation.id }
    );
    dispatch(deleteConversation(conversation.id)).then(() => {
      if (conversation.id === currentConversation?.id) {
        navigate(`/folder/${conversation.folderId ?? myFolder.id}`);
      }

      dispatch(setShowConversationsOnly(true));
      dispatch(getFolderAgnosticConversations({ pageAction: 'reset' }));
      dispatch(getConversations({ pageAction: 'reset' }));
      refetchConversations?.();
      dispatch(getGlobalSearchResults({ pageAction: 'reset' }));
      handleCloseUserMenu();
    });
  };

  const handleConversationMoved = (newFolderId: string) => {
    analytics.event(
      AnalyticsEvent.WM_NIGEL_CONVERSATION_MOVED,
      {
        id: conversation.id,
        value: newFolderId,
      },
      {
        conversationId: conversation.id,
        oldFolderId: conversation.folderId,
        newFolderId: newFolderId,
      }
    );
    dispatch(
      updateConversation({
        updatedFields: { folderId: newFolderId },
        conversationId: conversation.id,
      })
    ).then(() => {
      dispatch(getConversations({ pageAction: 'reset' }));
      refetchConversations?.();
      dispatch(setShowConversationsOnly(true));
      dispatch(getFolderAgnosticConversations({ pageAction: 'reset' }));
      dispatch(getGlobalSearchResults({ pageAction: 'reset' }));
      handleCloseUserMenu();
    });
  };

  const handleConversationShare = (newValue: boolean) => {
    analytics.event(
      AnalyticsEvent.WM_NIGEL_CONVERSATION_SHARED_LINK_CHECK,
      {
        id: conversation.id,
        value: newValue.toString(),
      },
      {
        conversationId: conversation.id,
        conversationWasShareable: conversation.shared,
        conversationNowShareable: newValue,
      }
    );

    setLocalShared(newValue);

    dispatch(
      updateConversation({ updatedFields: { shared: newValue }, conversationId: conversation.id })
    );
  };

  const handleConversationShareClosed = () => {
    analytics.event(
      AnalyticsEvent.WM_NIGEL_CONVERSATION_SHARED_LINK_CLOSE,
      {
        id: conversation.id,
      },
      {
        conversationId: conversation.id,
      }
    );

    dispatch(setShowConversationsOnly(true));
    dispatch(getConversations({ pageAction: 'reset' }));
    refetchConversations?.();
    dispatch(getFolderAgnosticConversations({ pageAction: 'reset' }));
    dispatch(getGlobalSearchResults({ pageAction: 'reset' }));
    setIsShareDialogOpen(false);
  };

  const handleConversationShareCopied = async () => {
    analytics.event(
      AnalyticsEvent.WM_NIGEL_CONVERSATION_SHARED_LINK_COPIED,
      {
        id: conversation.id,
      },
      {
        conversationId: conversation.id,
      }
    );

    await copyToClipboard(getHost() + `/chat/shared/${conversation.id}`);
  };

  const handleConversationDownload = () => {
    analytics.event(
      AnalyticsEvent.WM_NIGEL_CONVERSATION_DOWNLOAD,
      {
        id: conversation.id,
      },
      { conversationId: conversation.id }
    );
    dispatch(downloadFile(conversation.id)).then(
      (action: PayloadAction<DownloadConversationResponse | unknown>) => {
        const response = action.payload as DownloadConversationResponse;
        filesaver.saveAs(
          new Blob([response.conversation]),
          `${
            conversation.title.length > 50
              ? conversation.title.slice(0, 50).concat('...')
              : conversation.title
          }.md`
        );
      }
    );
  };

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        setIsOpen={setIsDeleteDialogOpen}
        onDelete={handleConversationDelete}
      />
      <NameDialog
        isOpen={isRenameDialogOpen}
        setIsOpen={setIsRenameDialogOpen}
        currentName={conversation.title}
        onRename={(newTitle: string) => handleConversationRename(newTitle)}
        type={'conversation'}
      />
      {conversation.appName !== AppEnum.DEAL_GPT && (
        <MoveToDialog
          folderId={conversation.folderId}
          folderName={conversation.folderName}
          isOpen={moveOpen}
          setIsOpen={setMoveOpen}
          onMove={(newFolderId: Folder['id']) => {
            handleConversationMoved(newFolderId);
          }}
        />
      )}
      {conversation.appName !== AppEnum.DEAL_GPT && (
        <ShareDialog
          isOpen={isShareDialogOpen}
          onSharedUpdate={(shared: boolean) => handleConversationShare(shared)}
          handleShareClosed={handleConversationShareClosed}
          handleShareCopied={handleConversationShareCopied}
          shared={localShared}
          copySuccess={copySuccess}
        />
      )}
      <Tooltip
        id={`open-settings-label-${conversation.id}`}
        title={<Typography variant="caption"> Conversation settings </Typography>}
        PopperProps={{ keepMounted: true }}
      >
        <IconButton
          onClick={handleOpenCardContextMenu}
          aria-labelledby={`open-settings-label-${conversation.id}`}
          sx={{ p: 0 }}
        >
          <MoreHoriz sx={{ color: '#070154', height: '20px', width: '20px' }} />
        </IconButton>
      </Tooltip>
      <Menu
        sx={{ mt: '16px' }}
        anchorEl={anchorContextMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorContextMenu)}
        onClose={handleCloseUserMenu}
      >
        {conversation.appName !== AppEnum.DEAL_GPT && (
          <MenuItem
            disabled={
              !(
                conversation.userId === currentUser?.id ||
                conversation.folderOwnerId === currentUser?.id
              )
            }
            onClick={() => {
              analytics.event(
                AnalyticsEvent.WM_NIGEL_CONVERSATION_MOVE_OPENED,
                {
                  id: conversation.id,
                },
                { conversationId: conversation.id }
              );
              setMoveOpen(true);
              handleCloseUserMenu();
            }}
          >
            <IconLabel>
              <SnippetFolderOutlined />
              Move to...
            </IconLabel>
          </MenuItem>
        )}
        <MenuItem onClick={handleConversationPinToggle}>
          <IconLabel>
            <PushPinOutlined
              sx={conversation.pinned ? { color: '#C4C4C4' } : { color: '#F900D3' }}
            />
            {conversation.pinned ? 'Unpin Chat' : 'Pin Chat'}
          </IconLabel>
        </MenuItem>
        <MenuItem onClick={handleConversationPrivacyToggle}>
          <IconLabel>
            {conversation.isPrivate ? <LockOpenOutlined /> : <LockOutlined />}
            {conversation.isPrivate ? 'Make Chat Public' : 'Make Chat Private'}
          </IconLabel>
        </MenuItem>
        <MenuItem
          disabled={
            !(
              conversation.userId === currentUser?.id ||
              conversation.folderOwnerId === currentUser?.id
            )
          }
          onClick={() => {
            analytics.event(
              AnalyticsEvent.WM_NIGEL_CONVERSATION_RENAME,
              {
                id: conversation.id,
              },
              { conversationId: conversation.id }
            );
            setIsRenameDialogOpen(true);
          }}
        >
          <IconLabel>
            <EditOutlined />
            Rename
          </IconLabel>
        </MenuItem>
        {conversation.appName !== AppEnum.DEAL_GPT && (
          <MenuItem
            onClick={() => {
              analytics.event(
                AnalyticsEvent.WM_NIGEL_CONVERSATION_SHARED_LINK,
                {
                  id: conversation.id,
                },
                {
                  conversationId: conversation.id,
                }
              );
              setIsShareDialogOpen(true);
            }}
            data-testid="menu-item-share"
          >
            <IconLabel>
              <LinkOutlined />
              Shared Link Settings
              <Chip
                component="span"
                label={localShared ? 'Shared' : 'Not Shared'}
                variant="outlined"
                color={localShared ? 'success' : 'default'}
                sx={{
                  backgroundColor: localShared ? '#E2FFE2' : 'default',
                  marginLeft: '5px',
                }}
              />
            </IconLabel>
          </MenuItem>
        )}
        <MenuItem onClick={handleConversationDownload}>
          <IconLabel>
            <FileDownloadOutlined /> Download Conversation (.md)
          </IconLabel>
        </MenuItem>
        <MenuItem
          disabled={
            !(
              conversation.userId === currentUser?.id ||
              conversation.folderOwnerId === currentUser?.id
            )
          }
          onClick={() => {
            analytics.event(
              AnalyticsEvent.WM_NIGEL_CONVERSATION_DELETE,
              {
                id: conversation.id,
              },
              { conversationId: conversation.id }
            );
            setIsDeleteDialogOpen(true);
          }}
        >
          <IconLabel>
            <DeleteOutlined sx={{ color: 'red' }} />
            Delete
          </IconLabel>
        </MenuItem>
      </Menu>
    </div>
  );
};

export default ChatOptionsMenu;
